import { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Layout, Menu, Button, Avatar, Dropdown, theme } from "antd";
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  FileTextOutlined,
  PlusCircleOutlined,
  UserOutlined,
  LogoutOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import type { User } from "../../../shared/types";

const { Header, Sider, Content } = Layout;

interface MainLayoutProps {
  auth: {
    user: User | null;
    logout: () => void;
  };
}

export default function MainLayout({ auth }: MainLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { token: themeToken } = theme.useToken();

  const menuItems = [
    {
      key: "/",
      icon: <HomeOutlined />,
      label: "案件列表",
    },
    {
      key: "/cases/create",
      icon: <PlusCircleOutlined />,
      label: "新建案件",
    },
  ];

  const userMenuItems = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: auth.user?.displayName,
      disabled: true,
    },
    {
      type: "divider" as const,
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "退出登录",
      danger: true,
    },
  ];

  const handleMenuClick = (info: { key: string }) => {
    navigate(info.key);
  };

  const handleUserMenuClick = (info: { key: string }) => {
    if (info.key === "logout") {
      auth.logout();
      navigate("/login");
    }
  };

  const getRoleLabel = (role: string) => {
    const roleMap: Record<string, string> = {
      user_a: "用户甲",
      user_b: "用户乙",
      admin: "管理员",
    };
    return roleMap[role] || role;
  };

  return (
    <Layout>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        style={{
          background: themeToken.colorBgContainer,
          height: "100vh",
          position: "fixed",
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 10,
        }}
      >
        <div
          className="flex items-center justify-center h-16 border-b border-gray-100"
          style={{ borderColor: themeToken.colorBorderSecondary }}
        >
          <FileTextOutlined
            style={{ fontSize: collapsed ? 24 : 28, color: "#1890ff" }}
          />
          {!collapsed && (
            <span className="ml-2 text-lg font-semibold text-gray-800">
              案件管理
            </span>
          )}
        </div>
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ borderRight: "none" }}
        />
      </Sider>
      <Layout style={{ marginLeft: collapsed ? 80 : 200, transition: "all 0.2s" }}>
        <Header
          style={{
            background: themeToken.colorBgContainer,
            padding: "0 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: "0 1px 4px rgba(0, 0, 0, 0.05)",
            position: "sticky",
            top: 0,
            zIndex: 9,
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ fontSize: 16, width: 48, height: 48 }}
          />
          <div className="flex items-center gap-4">
            <span className="text-gray-500 text-sm">
              {getRoleLabel(auth.user?.role || "")}
            </span>
            <Dropdown
              menu={{
                items: userMenuItems,
                onClick: handleUserMenuClick,
              }}
              placement="bottomRight"
            >
              <div className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 px-3 py-1 rounded-lg transition-colors">
                <Avatar
                  style={{ backgroundColor: "#1890ff" }}
                  icon={<UserOutlined />}
                />
                <span className="text-gray-700">
                  {auth.user?.displayName}
                </span>
              </div>
            </Dropdown>
          </div>
        </Header>
        <Content
          style={{
            margin: 24,
            minHeight: 280,
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
